import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsIsoDate,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs';
import type { UnallocatedFilters, UnallocatedSortField } from '../types/Checks';
import type { SortDirection } from '../types/Filters';
import { toApiDate } from '../utils/data';
import {
  allFiltersCleared,
  hasAnyFilter,
  idsOrNull,
  SORT_DIRECTIONS,
  selectionsFrom,
  textOrNull,
} from '../utils/filters';
import type { SelectedOption } from './useAllocationFilters';

const SORT_FIELDS = [
  'registrationNumber',
  'professional',
  'contractType',
  'profile',
  'contractStatus',
] as const satisfies readonly UnallocatedSortField[];

const PARSERS = {
  date: parseAsIsoDate,
  professionalIds: parseAsArrayOf(parseAsInteger),
  professionalNames: parseAsArrayOf(parseAsString),
  professionalSearch: parseAsString,
  profileSearch: parseAsString,
  contractTypeIds: parseAsArrayOf(parseAsInteger),
  contractStatusIds: parseAsArrayOf(parseAsInteger),
  sortBy: parseAsStringLiteral(SORT_FIELDS),
  sortDirection: parseAsStringLiteral(SORT_DIRECTIONS),
  page: parseAsInteger.withDefault(1),
  perPage: parseAsInteger.withDefault(20),
};

export type UnallocatedSearchKey = 'professionalSearch' | 'profileSearch';
export type UnallocatedIdsKey = 'contractTypeIds' | 'contractStatusIds';
export type UnallocatedFilterKey = 'professional' | UnallocatedSearchKey | UnallocatedIdsKey;

/** Rascunho do painel "Filtros avançados", comitado de uma vez no "Aplicar". */
export type UnallocatedAdvancedValues = {
  professionals: SelectedOption[];
  profileSearch: string;
  contractTypeIds: number[];
  contractStatusIds: number[];
};

const CLEARED_BY_KEY = {
  professional: { professionalIds: null, professionalNames: null },
  professionalSearch: { professionalSearch: null },
  profileSearch: { profileSearch: null },
  contractTypeIds: { contractTypeIds: null },
  contractStatusIds: { contractStatusIds: null },
} as const satisfies Record<UnallocatedFilterKey, object>;

const ALL_FILTERS_CLEARED = allFiltersCleared(CLEARED_BY_KEY);

/** Estado de "Sem alocação" na URL, no molde de `useAllocationFilters`; filtro ou data novos voltam à página 1. */
export function useUnallocatedFilters() {
  const [state, setState] = useQueryStates(PARSERS, { throttleMs: 300 });

  const filters: UnallocatedFilters = {
    date: toApiDate(state.date) ?? undefined,
    professionalIds: state.professionalIds ?? undefined,
    professionalSearch: state.professionalSearch ?? undefined,
    profileSearch: state.profileSearch ?? undefined,
    contractTypeIds: state.contractTypeIds ?? undefined,
    contractStatusIds: state.contractStatusIds ?? undefined,
    sortBy: state.sortBy ?? undefined,
    sortDirection: state.sortBy ? (state.sortDirection ?? 'asc') : undefined,
    page: state.page,
    perPage: state.perPage !== 20 ? state.perPage : undefined,
  };

  const professionals = selectionsFrom(state.professionalIds, state.professionalNames);

  const hasActiveFilters = hasAnyFilter(CLEARED_BY_KEY, state);

  return {
    filters,
    date: state.date,
    professionals,
    hasActiveFilters,
    setDate: (date: Date | null) => setState({ date, page: 1 }),
    setSearch: (key: UnallocatedSearchKey, value: string) =>
      setState({ [key]: textOrNull(value), page: 1 }),
    setIds: (key: UnallocatedIdsKey, ids: number[]) => setState({ [key]: idsOrNull(ids), page: 1 }),
    /** `'none'` volta à ordem do legado. */
    setSort: (field: UnallocatedSortField, direction: SortDirection | 'none') =>
      setState(
        direction === 'none'
          ? { sortBy: null, sortDirection: null }
          : { sortBy: field, sortDirection: direction },
      ),
    applyAdvanced: (values: UnallocatedAdvancedValues) =>
      setState({
        professionalIds: idsOrNull(values.professionals.map((option) => option.id)),
        professionalNames:
          values.professionals.length > 0
            ? values.professionals.map((option) => option.name)
            : null,
        profileSearch: textOrNull(values.profileSearch),
        contractTypeIds: idsOrNull(values.contractTypeIds),
        contractStatusIds: idsOrNull(values.contractStatusIds),
        page: 1,
      }),
    clearFilter: (key: UnallocatedFilterKey) => setState({ ...CLEARED_BY_KEY[key], page: 1 }),
    /** Limpa os filtros, não a ordenação nem a data. */
    clearFilters: () => setState({ ...ALL_FILTERS_CLEARED, page: 1 }),
    perPage: state.perPage,
    setPerPage: (perPage: number) => setState({ perPage, page: 1 }),
    goToPage: (page: number) => setState({ page }),
  };
}

export type UnallocatedFilterState = ReturnType<typeof useUnallocatedFilters>;
